import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";

import { tenantContext } from "../lib/tenant";

/**
 * Auto-switch Clerk's active organization to the tenant whose
 * subdomain the user is currently on.
 *
 * The bug this fixes: Clerk users with "personal account" mode
 * enabled default to no active org even when they're a member of
 * one. On a tenant subdomain (riyadh.solvait.ai, etc.) the JWT
 * they mint has `org_id: null` → the backend's tenant resolver
 * can't match → every API call 401s → workspace shell renders empty.
 *
 * The fix: when the user lands on a tenant subdomain, look at their
 * Clerk org memberships, find the one whose `organization.slug`
 * matches the URL subdomain (we always create the Clerk org with the
 * same slug as the tenant), and call `clerk.setActive(...)` on it.
 * Reload after success so React Query's cached 401 responses get
 * dropped and every query refetches with the org-aware JWT.
 *
 * Guards:
 *   - Apex (no tenant subdomain) → no-op.
 *   - User isn't a member of the matching org → no-op (they
 *     genuinely don't have access; let the 401 surface).
 *   - Active org already matches → no-op.
 *   - Once per page-load: the `triggered` ref prevents retry storms
 *     if setActive throws (we re-enable on the catch path so a
 *     transient failure can self-heal on a later re-render).
 *
 * Renders nothing. Sibling to `ApiTokenBridge` inside `<ClerkProvider>`.
 */
export function OrgAutoSwitch() {
  const { isLoaded: userLoaded, isSignedIn, user } = useUser();
  const { orgId } = useAuth();
  const clerk = useClerk();
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    if (!userLoaded || !isSignedIn || !user) return;
    const slug = tenantContext().slug;
    if (!slug) return; // apex — nothing to switch to.

    const matching = user.organizationMemberships?.find(
      (m) => m.organization.slug === slug,
    );
    if (!matching) return; // user isn't a member of this tenant's org
    if (orgId === matching.organization.id) return; // already active

    triggered.current = true;
    clerk
      .setActive({ organization: matching.organization.id })
      .then(() => {
        // Reload so React Query drops cached 401s + refetches with
        // the new org-bearing JWT. setActive returns once Clerk's
        // session is updated; the next getToken() yields a JWT
        // with the new org_id.
        window.location.reload();
      })
      .catch((e) => {
        console.warn("OrgAutoSwitch: setActive failed", e);
        triggered.current = false;
      });
  }, [userLoaded, isSignedIn, user, orgId, clerk]);

  return null;
}
